import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import * as api from "../../api/betsApi";
import type {
  BetSlipType,
  GroupedBetSlipsState,
} from "../../types/betSlipType";
import type { EventRunnerType } from "../../types/eventType";
import { BetSlipStates, maxNumberOfBetsInBetslip } from "../../utils/const";

const BetSlipListModel = {
  state: {
    currentBetsCount: 0,
    betSlipList: [] as Array<BetSlipType>,
    isOrangeFlashActive: false,
    betslipFull: false,
    collapseAllBetSlips: false,
    eventIds: [] as Array<number>,
    marketIds: [] as Array<number>,
    runnerIds: [] as Array<number>,
    noOfBetSlipGroupdedEvents: [] as Array<any>,
    betSlipsGroupedByEventCount: 0,
    addedBetSlipsTotal: 0,
    numberOfMultiplesNotAvailable: 0,
  },
  reducers: {
    /**
     * toggle expand collapse all bet slip groups in bet slip panel with expand/collapse bets feature
     */
    toggleExpandCollapseAllBets(state: Record<string, any>) {
      return {
        ...state,
        collapseAllBetSlips: !state.collapseAllBetSlips,
      };
    },
    /**
     * collapse or expand all bet slip groups in bet slip panel
     */
    collapseOrExpandAllBets(state: Record<string, any>, payload: boolean) {
      return {
        ...state,
        collapseAllBetSlips: payload,
      };
    },
    /**
     * if mobile view betslip panel selected, set as default all bet slips in bet slip panel to expanded
     * this is because expand/collapse all feature not available in mobile view
     */
    setMobileDefaultCollapseAllBets(state: Record<string, any>) {
      return {
        ...state,
        collapseAllBetSlips: false,
      };
    },
    /**
     * update number of BetSlips Grouped By Event in the bet slip panel
     */
    updateBetSlipsGroupedByEventCount(
      state: Record<string, any>,
      payload: number
    ) {
      return {
        ...state,
        betSlipsGroupedByEventCount: payload,
      };
    },
    /**
     * set the number of uniq events in bet slip panel
     */
    updateBetSlipGroupedEvents(state: Record<string, any>, payload) {
      return {
        ...state,
        noOfBetSlipGroupdedEvents: payload,
      };
    },
    /**
     * increaseNumberOfMultiplesNotAvailable
     */
    increaseNumberOfMultiplesNotAvailable(state: Record<string, any>) {
      return {
        ...state,
        numberOfMultiplesNotAvailable: state.numberOfMultiplesNotAvailable + 1,
      };
    },
    /**
     * decreaseNumberOfMultiplesNotAvailable
     */
    decreaseNumberOfMultiplesNotAvailable(state: Record<string, any>) {
      return {
        ...state,
        numberOfMultiplesNotAvailable: state.numberOfMultiplesNotAvailable - 1,
      };
    },
    /**
     * keep track of the state of grouped bet slip events that are being expanded/collapsed in the bet slip panel
     */
    toggleNumberOfBetSlipGroupedEvents(
      state: Record<string, any>,
      payload: GroupedBetSlipsState
    ) {
      return {
        ...state,
        noOfBetSlipGroupdedEvents: payload,
      };
    },
    /**
     * does not allow adding the same betSlip twice
     *
     * looks up current BetSlip list and finds
     * highest id to then give the new BetSlip
     * the next higher id before storing it
     */
    addBetSlip(state: Record<string, any>, payload: BetSlipType): any {
      let betSlipList = cloneDeep(state.betSlipList);
      let eventIds = cloneDeep(state.eventIds);
      let marketIds = cloneDeep(state.marketIds);
      let runnerIds = cloneDeep(state.runnerIds);
      // check if this runner has already been added
      // if so highlight it by setting it to latest - shows orange border animation
      // and set isOrangeFlashActive flag, which is used to show orange border for
      // number of bets text in the bet drawer header
      let alreadyAddedBetslip = betSlipList.find(
        betSlip =>
          betSlip.markets[0].runners[0].id === payload.markets[0].runners[0].id
      );

      let alreadyAddedExchangeBetslip = betSlipList.find(
        betSlip => betSlip.id === payload.id
      );

      if (alreadyAddedBetslip && !payload.isExchange) {
        alreadyAddedBetslip.latest = true;
        return {
          ...state,
          betSlipList: betSlipList,
          isOrangeFlashActive: true,
        };
      } else if (alreadyAddedExchangeBetslip && payload.isExchange) {
        // extra step if user selects different back/lay price in same exchange market runner
        let updatedExchagePrice = betSlipList.map(betSlip => {
          if (
            betSlip.id === payload.id &&
            betSlip.isExchange === payload.isExchange
          ) {
            alreadyAddedExchangeBetslip.latest = true;
            // pass local id to new exchange bet replacing old bets
            payload.localId = betSlip.localId;
            return payload;
          }
          return betSlip;
        });

        if (alreadyAddedExchangeBetslip.latest) {
          return {
            ...state,
            betSlipList: updatedExchagePrice,
            isOrangeFlashActive: true,
          };
        }
      }

      if (betSlipList.length === maxNumberOfBetsInBetslip) {
        return { ...state, betslipFull: true, isOrangeFlashActive: true };
      }

      let betSlipId =
        betSlipList.length === 0
          ? 0
          : Math.max(...betSlipList.map(b => b.localId));
      let betSlip = { ...payload };
      betSlip.localId = betSlipId + 1;
      // Set bet slip to active to show it expanded by default
      betSlip.isActive = true;
      betSlip.latest = true;
      const eventId = get(betSlip, ["id"]);

      if (eventId) {
        eventIds.push(eventId);
      }

      const marketId = get(betSlip, ["markets", [0], "id"]);

      if (marketId) {
        marketIds.push(marketId);
      }

      const runnerId = get(betSlip, ["markets", [0], "runners", [0], "id"]);

      if (runnerId) {
        runnerIds.push(runnerId);
      }

      betSlipList.push(betSlip);
      const betslipFull =
        betSlipList.length === maxNumberOfBetsInBetslip ? true : false;

      return {
        ...state,
        betSlipList: betSlipList,
        isOrangeFlashActive: true,
        betslipFull,
        eventIds,
        marketIds,
        runnerIds,
        addedBetSlipsTotal: state.addedBetSlipsTotal + 1,
      };
    },

    /**
     * looks up BetSlip with id equal to payload
     * and returns state without the BetSlip with given id
     */
    removeBetSlip(state: Record<string, any>, payload: number): any {
      let betSlipList = cloneDeep(state.betSlipList);
      let eventIds = cloneDeep(state.eventIds);
      let marketIds = cloneDeep(state.marketIds);
      let runnerIds = cloneDeep(state.runnerIds);
      const betSlip = betSlipList.find(b => b.localId === payload);
      const eventId = get(betSlip, ["id"]);

      if (eventId) {
        eventIds.indexOf(eventId);
        const index = eventIds.indexOf(eventId);

        if (index > -1) {
          eventIds.splice(index, 1);
        }
      }

      const marketId = get(betSlip, ["markets", [0], "id"]);

      if (marketId) {
        marketIds.indexOf(marketId);
        const index = marketIds.indexOf(marketId);

        if (index > -1) {
          marketIds.splice(index, 1);
        }
      }

      const runnerId = get(betSlip, ["markets", [0], "runners", [0], "id"]);

      if (runnerId) {
        runnerIds.indexOf(runnerId);
        const index = runnerIds.indexOf(runnerId);

        if (index > -1) {
          runnerIds.splice(index, 1);
        }
      }

      let validBetSlipList = betSlipList.filter(
        betSlip => betSlip.localId !== payload
      );
      const betslipFull =
        betSlipList.length <= maxNumberOfBetsInBetslip ? false : true;

      return {
        ...state,
        betSlipList: validBetSlipList,
        betslipFull,
        eventIds,
        marketIds,
        runnerIds,
        addedBetSlipsTotal: state.addedBetSlipsTotal - 1,
      };
    },

    /**
     * finds betSlip with id equal to payload
     * and replaces it with the payload
     */
    updateBetSlip: (state: Record<string, any>, payload: BetSlipType): any => {
      let betSlipList = cloneDeep(state.betSlipList);
      let foundBetSlip = betSlipList.find(
        betSlip => betSlip.localId === payload.localId
      );

      if (foundBetSlip != null) {
        // Only update the bet slip of the state is not finished
        if (foundBetSlip.state !== BetSlipStates.FINISHED) {
          Object.assign(foundBetSlip, payload);
        }
      }

      return { ...state, betSlipList: betSlipList };
    },

    /**
     *
     * remove bets from betSlipList with state of FINISHED
     */
    removeFinishedBets: (
      state: Record<string, any>,
      payload: BetSlipType
    ): any => {
      let betSlipList = cloneDeep(state.betSlipList);
      let updatedBetSlipList = betSlipList.filter(
        betSlip => betSlip.state !== "FINISHED"
      );
      return { ...state, betSlipList: updatedBetSlipList };
    },

    /**
     * Update the whole list
     * payload: { betSlipList }
     */
    updateBetSlipList: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, betSlipList: payload.betSlipList };
    },

    /**
     * Set the current Bets count for the nav reports badge
     */
    setCurrentBetsCount: (state: Record<string, any>, payload: number): any => {
      return { ...state, currentBetsCount: payload };
    },

    /**
     * Empty the bet slip list, apart from bets that are PENDING
     *  - bets are in PENDING state while waiting for a response after being submitted.
     */
    purgeBetSlipList: (state: Record<string, any>): any => {
      let betSlipList = cloneDeep(state.betSlipList);
      let processingBetsOnly = [];
      betSlipList.forEach(betslip => {
        betslip.state === "PENDING" && processingBetsOnly.push(betslip);
      });
      return {
        ...state,
        betSlipList: processingBetsOnly,
        betslipFull: false,
        noOfBetSlipGroupdedEvents: [],
        betSlipsGroupedByEventCount: 0,
        addedBetSlipsTotal: 0,
        eventIds: [],
        marketIds: [],
        runnerIds: [],
        collapseAllBetSlips: false,
        numberOfMultiplesNotAvailable: 0,
      };
    },

    /**
     * Used for showing the  border animation in betdrawer header
     * When true, causes a css class to be added
     */
    setFlashFlag: (state: Record<string, any>): any => {
      return { ...state, isOrangeFlashActive: false };
    },

    /**
     * Update the prices for a betslip from api poll
     */
    updateBetSlipPrices: (
      state: Record<string, any>,
      payload: EventRunnerType
    ): any => {
      let betSlipList = cloneDeep(state.betSlipList);
      const betslipToBeUpdated = betSlipList.find(betslip => {
        const betslipRunnerid = get(betslip, [
          "markets",
          [0],
          "runners",
          [0],
          "id",
        ]);
        return betslipRunnerid && betslipRunnerid === payload.id;
      });

      if (betslipToBeUpdated) {
        betslipToBeUpdated.markets[0].runners[0]["aggregatedPrices"] =
          payload["aggregatedPrices"];
      }

      return { ...state, betSlipList: betSlipList };
    },

    /**
     * Update prices for all betslips from api poll
     * payload is a list of runners
     *
     */
    updateSingleBetSlipPrices: (
      state: Record<string, any>,
      payload: EventRunnerType[]
    ): any => {
      let betSlipList = cloneDeep(state.betSlipList);
      // update each betslip with the new aggregatedPrices
      betSlipList.forEach(betslip => {
        payload.length > 0 &&
          payload.forEach((update: any) => {
            // BET-12714 AP@ update from id to runnerId
            if (betslip.markets[0].runners[0].id === update.runnerId) {
              betslip.markets[0].runners[0]["aggregatedPrices"] =
                update["aggregatedPrices"];
              betslip.markets[0].runners[0].isWebsocketPrice = false;
            }
          });
      });
      return { ...state, betSlipList: betSlipList };
    },
  },
  effects: {
    async loadCurrentBetsCount(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      api.fetchCurrentBetsCount().then(resp => {
        this.setCurrentBetsCount(resp.total);
      });
    },

    async loadProcessingBets(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ) {
      api
        .fetchProcessingBets()
        .then(resp => {
          resp.bets.forEach((bet, i) => {
            let marketName = bet.market["marketName"];
            this.addBetSlip({
              betId: bet.bet["betId"],
              id: bet.event["eventId"],
              name: bet.event["eventName"],
              translatedName: bet.event["eventName"],
              processingBet: true,
              stake: bet.bet.stake,
              state: "PENDING",
              potentialProfit: bet.bet["potentialProfit"],
              markets: [
                {
                  id: bet.market["marketId"],
                  name: bet.market["marketName"],
                  period: bet.market["marketPeriod"],
                  type: bet.market["marketType"],
                  runners: [
                    {
                      id: bet.runner["runnerId"],
                      name: bet.runner["runnerName"],
                      aggregatedPrices: [
                        {
                          availableAmount: 0,
                          decimalOdds: bet.bet.odds,
                          odds: bet.bet.odds,
                        },
                      ],
                      handicap: Number(
                        marketName.substring(
                          marketName.indexOf("(") + 1,
                          marketName.indexOf(")")
                        )
                      ),
                    },
                  ],
                },
              ],
            });
          });
        })
        .catch(error => {
          throw error;
        });
    },
  },
};
export default BetSlipListModel;
